import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  useCreateEventMutation,
  useUploadEventMediaMutation,
  useUpdateEventMutation,
} from "../../../features/events/eventsApiSlice";
import { setDialog } from "../../../features/dialog/dialogSlice";
import Iconify from "../../iconify/Iconify";
import StepEventType from "./StepEventType";
import StepBasicInfo from "./StepBasicInfo";
import StepVisibility from "./StepVisibility";
import StepPricingStandard from "./StepPricingStandard";
import StepRegistration from "./StepRegistration";
import StepSessions from "./StepSessions";
import StepPricingBooking from "./StepPricingBooking";
import StepExtras from "./StepExtras";
import StepAttachments from "./StepAttachments";
import StepSummary from "./StepSummary";
import { validateBookingPackageRulesRestPricing } from "../booking/bookingPricingUtils";

const STANDARD_STEPS = [
  { key: "eventType", label: "Event-Typ" },
  { key: "basicInfo", label: "Grundlagen" },
  { key: "visibility", label: "Sichtbarkeit & Anmeldung" },
  { key: "pricingStandard", label: "Preise" },
  { key: "attachments", label: "Anhänge" },
  { key: "summary", label: "Zusammenfassung" },
];

const BOOKING_STEPS = [
  { key: "eventType", label: "Event-Typ" },
  { key: "basicInfo", label: "Grundlagen" },
  { key: "registration", label: "Anmeldung & Sichtbarkeit" },
  { key: "sessions", label: "Sessions" },
  { key: "pricingBooking", label: "Preise & Rabatte" },
  { key: "extras", label: "Extras" },
  { key: "attachments", label: "Anhänge" },
  { key: "summary", label: "Zusammenfassung" },
];

const STEP_ERROR_FIELDS = {
  eventType: ["registrationType"],
  basicInfo: ["eventName", "eventDateBegin", "eventDateEnd"],
  registration: ["registrationConfig"],
  sessions: ["sessions"],
  pricingBooking: ["pricingRules", "discountRules"],
  extras: ["customFields", "requiredConsents"],
  pricingStandard: ["pricingOptions", "siblingDiscount"],
};

const DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT =
  "Ich ermächtige den Veranstalter, den fälligen Betrag per SEPA-Lastschrift " +
  "von meinem Konto einzuziehen. Zugleich weise ich mein Kreditinstitut an, " +
  "die vom Veranstalter auf mein Konto gezogenen Lastschriften einzulösen. " +
  "Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, " +
  "die Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit " +
  "meinem Kreditinstitut vereinbarten Bedingungen.";
const DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT =
  "Hallo {{bookerFirstName}} {{bookerLastName}},\n\n" +
  'vielen Dank für Ihre Anmeldung zu "{{eventName}}".\n' +
  "Ihre Buchungsnummer lautet: {{registrationNumber}}.\n" +
  "Status Ihrer Anmeldung: {{registrationStatus}}.\n\n" +
  "Ihre Buchungsdaten:\n" +
  "- Teilnehmer: {{participantNames}}\n" +
  "- Sessions: {{sessionLabels}}\n" +
  "- Gesamtbetrag: {{totalAmount}}\n" +
  "- Zahlungsart: {{paymentMethod}}\n\n" +
  "Zahlungsinformationen:\n" +
  "{{paymentText}}\n\n" +
  "Link zu Ihrer Anmeldung: {{editLink}}\n\n" +
  "Viele Grüße\n" +
  "{{tenantName}}";

// Erkennt preisrelevante Änderungen an Preisregeln/Rabattregeln
const hasPricingChanged = (originalEvent, currentFormData) => {
  if (!originalEvent || !currentFormData) return false;
  if (originalEvent.registrationType !== "booking") return false;

  const origRules = originalEvent.pricingRules || [];
  const newRules = currentFormData.pricingRules || [];

  const extractPriceFields = (rules) =>
    rules.map((r) => ({
      ruleId: r.ruleId,
      type: r.type,
      amount: r.amount,
      memberDiscount: r.memberDiscount,
      minSessions: r.minSessions,
      sessionIds: [...(r.sessionIds || [])].sort(),
    }));

  const origFields = JSON.stringify(extractPriceFields(origRules));
  const newFields = JSON.stringify(extractPriceFields(newRules));

  if (origFields !== newFields) return true;

  const origDiscount = JSON.stringify(
    originalEvent.registrationConfig?.discountRules || {},
  );
  const newDiscount = JSON.stringify(
    currentFormData.registrationConfig?.discountRules || {},
  );

  return origDiscount !== newDiscount;
};

const DEFAULT_FORM_DATA = {
  registrationType: "legacy",
  eventName: "",
  eventType: "EVENT",
  status: "geplant",
  eventDateBegin: "",
  eventDateEnd: "",
  description: "",
  locationName: "",
  city: "",
  zip: "",
  street: "",
  country: "",
  maxParticipants: "",
  tags: "",
  isPublic: false,
  allowSelfRegistration: true,
  password: "",
  isFeeBased: false,
  feeAmount: "",
  pricingOptions: [],
  siblingDiscount: {
    enabled: false,
    type: "percentage",
    value: 0,
  },
  sessions: [],
  pricingRules: [],
  registrationConfig: {
    isPublicRegistrationEnabled: false,
    requiresApproval: false,
    registrationDeadline: "",
    maxRegistrations: "",
    eventPreset: "custom",
    capacityMode: "registrations",
    customFields: [],
    requiredConsents: [],
    bankTransferInfo: {
      enabled: false,
      customText: "",
    },
    sepaDirectDebitConsentText: DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT,
    publicBookingConfirmationText: DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT,
    publicBookingConfirmationTemplateMode: "text",
    publicBookingConfirmationHtmlTemplate: "",
    discountRules: {
      siblingDiscount: {
        enabled: false,
        percentage: 0,
      },
      earlyBirdDiscount: {
        enabled: false,
        type: "percentage",
        percentage: 0,
        amount: 0,
        deadline: "",
      },
    },
  },
};

const LEGACY_TYPE_RESET_FIELDS = [
  "pricingOptions",
  "siblingDiscount",
  "isFeeBased",
  "feeAmount",
];
const BOOKING_TYPE_RESET_FIELDS = [
  "sessions",
  "pricingRules",
  "registrationConfig",
  "registration",
  "discountRules",
  "customFields",
  "requiredConsents",
];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

const toStringOrEmpty = (value) => (value == null ? "" : String(value));

const toBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "ja"].includes(normalized)) return true;
    if (["false", "0", "no", "nein"].includes(normalized)) return false;
  }
  if (typeof value === "number") return value > 0;
  return fallback;
};

const toNumberOr = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const cloneRegistrationConfig = (registrationConfig = {}) => {
  const discountRules = registrationConfig?.discountRules || {};

  return {
    ...DEFAULT_FORM_DATA.registrationConfig,
    ...registrationConfig,
    customFields: Array.isArray(registrationConfig.customFields)
      ? registrationConfig.customFields.map((field) => ({
          ...field,
          options: Array.isArray(field.options)
            ? [...field.options]
            : String(field.options || "")
                .split(",")
                .map((entry) => entry.trim())
                .filter(Boolean),
          optionsInput:
            typeof field.optionsInput === "string"
              ? field.optionsInput
              : Array.isArray(field.options)
              ? field.options.join(", ")
              : String(field.options || ""),
          showCondition: {
            dependsOnFieldId: field?.showCondition?.dependsOnFieldId || "",
            dependsOnValue: field?.showCondition?.dependsOnValue || "",
          },
        }))
      : [],
    requiredConsents: Array.isArray(registrationConfig.requiredConsents)
      ? registrationConfig.requiredConsents.map((consent) => ({ ...consent }))
      : [],
    bankTransferInfo: {
      ...DEFAULT_FORM_DATA.registrationConfig.bankTransferInfo,
      ...(registrationConfig.bankTransferInfo || {}),
    },
    publicBookingConfirmationText:
      typeof registrationConfig.publicBookingConfirmationText === "string"
        ? registrationConfig.publicBookingConfirmationText
        : DEFAULT_FORM_DATA.registrationConfig.publicBookingConfirmationText,
    publicBookingConfirmationTemplateMode:
      registrationConfig.publicBookingConfirmationTemplateMode === "html"
        ? "html"
        : "text",
    publicBookingConfirmationHtmlTemplate:
      typeof registrationConfig.publicBookingConfirmationHtmlTemplate ===
      "string"
        ? registrationConfig.publicBookingConfirmationHtmlTemplate
        : "",
    discountRules: {
      siblingDiscount: {
        ...DEFAULT_FORM_DATA.registrationConfig.discountRules.siblingDiscount,
        ...(discountRules.siblingDiscount || {}),
      },
      earlyBirdDiscount: {
        ...DEFAULT_FORM_DATA.registrationConfig.discountRules.earlyBirdDiscount,
        ...(discountRules.earlyBirdDiscount || {}),
      },
    },
  };
};

const createDefaultRegistrationConfig = () =>
  cloneRegistrationConfig(DEFAULT_FORM_DATA.registrationConfig);

const createTypeSpecificReset = (nextType) => {
  if (nextType === "booking") {
    return {
      isFeeBased: false,
      feeAmount: "",
      pricingOptions: [],
      siblingDiscount: { ...DEFAULT_FORM_DATA.siblingDiscount },
    };
  }

  return {
    sessions: [],
    pricingRules: [],
    registrationConfig: createDefaultRegistrationConfig(),
  };
};

const getTypeSwitchDataLossHints = (formData, nextType) => {
  const currentType = formData?.registrationType || "legacy";
  if (!["legacy", "booking"].includes(nextType) || currentType === nextType) {
    return [];
  }

  if (currentType === "legacy" && nextType === "booking") {
    const hints = [];
    if (toBoolean(formData?.isFeeBased, false)) {
      hints.push("Standard-Preislogik");
    }
    if (
      Array.isArray(formData?.pricingOptions) &&
      formData.pricingOptions.length > 0
    ) {
      hints.push("Preisoptionen");
    }
    if (toBoolean(formData?.siblingDiscount?.enabled, false)) {
      hints.push("Geschwisterrabatt (Standard-Event)");
    }
    return hints;
  }

  const registrationConfig = formData?.registrationConfig || {};
  const discountRules = registrationConfig.discountRules || {};
  const hints = [];
  if (Array.isArray(formData?.sessions) && formData.sessions.length > 0) {
    hints.push("Sessions");
  }
  if (
    Array.isArray(formData?.pricingRules) &&
    formData.pricingRules.length > 0
  ) {
    hints.push("Preisregeln");
  }
  if (
    Array.isArray(registrationConfig.customFields) &&
    registrationConfig.customFields.length > 0
  ) {
    hints.push("Zusatzfelder");
  }
  if (
    Array.isArray(registrationConfig.requiredConsents) &&
    registrationConfig.requiredConsents.length > 0
  ) {
    hints.push("Einwilligungen");
  }
  if (
    toBoolean(discountRules?.siblingDiscount?.enabled, false) ||
    toBoolean(discountRules?.earlyBirdDiscount?.enabled, false)
  ) {
    hints.push("Buchungsrabatte");
  }
  if (toBoolean(registrationConfig?.bankTransferInfo?.enabled, false)) {
    hints.push("Banktransfer-Hinweise");
  }
  return hints;
};

const buildInitialFormData = (initialEvent) => {
  if (!initialEvent) {
    return { ...DEFAULT_FORM_DATA };
  }

  const isFeeBased = toBoolean(initialEvent.isFeeBased, false);
  const initialPricingOptions = Array.isArray(initialEvent.pricingOptions)
    ? initialEvent.pricingOptions.map((option) => ({
        optionId: option.optionId,
        label: option.label || "",
        description: option.description || "",
        amount: toNumberOr(option.amount, 0),
      }))
    : [];
  const fallbackPricingOptions =
    isFeeBased &&
    initialPricingOptions.length === 0 &&
    toNumberOr(initialEvent.feeAmount, 0) > 0
      ? [
          {
            optionId: "",
            label: "Teilnahmegebühr",
            description: "",
            amount: toNumberOr(initialEvent.feeAmount, 0),
          },
        ]
      : [];
  const derivedFeeAmount =
    toNumberOr(initialEvent.feeAmount, 0) > 0
      ? toNumberOr(initialEvent.feeAmount, 0)
      : toNumberOr(initialPricingOptions?.[0]?.amount, 0);
  const initialRegistrationConfig = cloneRegistrationConfig(
    initialEvent.registrationConfig,
  );

  return {
    ...DEFAULT_FORM_DATA,
    registrationType: initialEvent.registrationType || "legacy",
    eventName: initialEvent.eventName || "",
    eventType: initialEvent.eventType || "EVENT",
    status: initialEvent.status || "geplant",
    eventDateBegin: toDateTimeLocal(initialEvent.eventDateBegin),
    eventDateEnd: toDateTimeLocal(initialEvent.eventDateEnd),
    description: initialEvent.description || "",
    locationName: initialEvent.locationName || "",
    city: initialEvent.city || "",
    zip: initialEvent.zip || "",
    street: initialEvent.street || "",
    country: initialEvent.country || "",
    maxParticipants:
      initialEvent.maxParticipants != null
        ? String(initialEvent.maxParticipants)
        : "",
    tags: Array.isArray(initialEvent.tags)
      ? initialEvent.tags.join(", ")
      : initialEvent.tags || "",
    isPublic: toBoolean(initialEvent.isPublic, false),
    allowSelfRegistration: toBoolean(initialEvent.allowSelfRegistration, true),
    password: initialEvent.password || "",
    isFeeBased,
    feeAmount: derivedFeeAmount > 0 ? String(derivedFeeAmount) : "",
    pricingOptions:
      initialPricingOptions.length > 0
        ? initialPricingOptions
        : fallbackPricingOptions,
    siblingDiscount: {
      enabled: toBoolean(initialEvent?.siblingDiscount?.enabled, false),
      type:
        initialEvent?.siblingDiscount?.type === "fixed"
          ? "fixed"
          : "percentage",
      value: toNumberOr(initialEvent?.siblingDiscount?.value, 0),
    },
    sessions: Array.isArray(initialEvent.sessions)
      ? initialEvent.sessions.map((session, index) => ({
          ...session,
          dateStart: toDateTimeLocal(session?.dateStart),
          dateEnd: toDateTimeLocal(session?.dateEnd),
          maxParticipants:
            session?.maxParticipants != null
              ? String(session.maxParticipants)
              : "",
          sortOrder: Number.isFinite(Number(session?.sortOrder))
            ? Number(session.sortOrder)
            : index,
        }))
      : [],
    pricingRules: Array.isArray(initialEvent.pricingRules)
      ? initialEvent.pricingRules.map((rule) => ({
          ...rule,
          amount: toNumberOr(rule?.amount, 0),
          memberDiscount: toNumberOr(rule?.memberDiscount, 0),
          sessionIds: Array.isArray(rule?.sessionIds)
            ? [...rule.sessionIds]
            : [],
        }))
      : [],
    registrationConfig: {
      ...initialRegistrationConfig,
      registrationDeadline: toDateTimeLocal(
        initialRegistrationConfig.registrationDeadline,
      ),
      maxRegistrations:
        initialRegistrationConfig.maxRegistrations != null &&
        initialRegistrationConfig.maxRegistrations !== ""
          ? String(initialRegistrationConfig.maxRegistrations)
          : "",
      eventPreset: String(initialRegistrationConfig.eventPreset || "custom"),
      capacityMode:
        initialRegistrationConfig.capacityMode === "participants"
          ? "participants"
          : "registrations",
      discountRules: {
        siblingDiscount: {
          ...initialRegistrationConfig.discountRules.siblingDiscount,
          percentage: toNumberOr(
            initialRegistrationConfig.discountRules.siblingDiscount?.percentage,
            0,
          ),
        },
        earlyBirdDiscount: {
          ...initialRegistrationConfig.discountRules.earlyBirdDiscount,
          type:
            initialRegistrationConfig.discountRules.earlyBirdDiscount?.type ===
            "fixed"
              ? "fixed"
              : "percentage",
          percentage: toNumberOr(
            initialRegistrationConfig.discountRules.earlyBirdDiscount
              ?.percentage,
            0,
          ),
          amount: toNumberOr(
            initialRegistrationConfig.discountRules.earlyBirdDiscount?.amount,
            0,
          ),
          deadline: toDateTimeLocal(
            initialRegistrationConfig.discountRules.earlyBirdDiscount?.deadline,
          ),
        },
      },
    },
  };
};

function StepPlaceholder({ label, phase }) {
  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 2,
        p: 3,
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
      }}
    >
      <Typography variant="h6">{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {phase
          ? `Dieser Schritt wird in ${phase} umgesetzt.`
          : "Dieser Schritt ist aktuell nicht verfügbar."}
      </Typography>
    </Box>
  );
}

StepPlaceholder.propTypes = {
  label: PropTypes.string.isRequired,
  phase: PropTypes.string,
};

export default function EventFormStepper({
  open,
  initialEvent,
  onSaved,
  onClose,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isEdit = Boolean(initialEvent);

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [uploadEventMedia, { isLoading: isUploadingAttachments }] =
    useUploadEventMediaMutation();

  const [formData, setFormData] = useState(() =>
    buildInitialFormData(initialEvent),
  );
  const [activeStep, setActiveStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([]);
  const [attachmentVisibility, setAttachmentVisibility] = useState(false);
  const [showPropagateDialog, setShowPropagateDialog] = useState(false);

  const steps = useMemo(
    () =>
      formData.registrationType === "booking" ? BOOKING_STEPS : STANDARD_STEPS,
    [formData.registrationType],
  );

  const typeLockedReason =
    "Der Event-Typ kann nicht mehr gewechselt werden, weil bereits Teilnehmer oder Buchungen vorhanden sind.";

  const isRegistrationTypeLocked = useMemo(() => {
    if (!isEdit) return false;

    const legacyParticipants = Array.isArray(initialEvent?.participants)
      ? initialEvent.participants.length
      : Number(initialEvent?.participantCount || 0);
    const legacyAttendees = Array.isArray(initialEvent?.hasAttendees)
      ? initialEvent.hasAttendees.length
      : 0;
    const bookingRegistrations = Number(
      initialEvent?.bookingRegistrationCount || 0,
    );
    const bookingParticipants = Number(
      initialEvent?.bookingParticipantCount || 0,
    );

    return (
      legacyParticipants > 0 ||
      legacyAttendees > 0 ||
      bookingRegistrations > 0 ||
      bookingParticipants > 0
    );
  }, [initialEvent, isEdit]);

  useEffect(() => {
    if (!open) return;
    setFormData(buildInitialFormData(initialEvent));
    setActiveStep(0);
    setMaxVisitedStep(0);
    setErrors({});
    setSubmitError("");
    setPendingAttachmentFiles([]);
    setAttachmentVisibility(false);
  }, [initialEvent, open]);

  useEffect(() => {
    if (activeStep <= steps.length - 1) return;
    setActiveStep(steps.length - 1);
  }, [activeStep, steps.length]);

  const currentStep = steps[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const isSaving = isCreating || isUpdating || isUploadingAttachments;

  const clearFieldErrors = (fields = []) => {
    if (!Array.isArray(fields) || fields.length === 0) return;
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        delete next[field];
      });
      return next;
    });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldErrors([field]);
    if (field === "eventDateBegin" || field === "eventDateEnd") {
      clearFieldErrors(["eventDateBegin", "eventDateEnd"]);
    }
    if (field === "registrationType") {
      clearFieldErrors(["registrationType"]);
    }
    if (field === "isFeeBased") {
      clearFieldErrors(["pricingOptions", "siblingDiscount"]);
    }
    if (field === "sessions") {
      clearFieldErrors(["sessions", "pricingRules"]);
    }
    if (field === "pricingRules") {
      clearFieldErrors(["pricingRules"]);
    }
    if (field === "registrationConfig") {
      clearFieldErrors([
        "registrationConfig",
        "discountRules",
        "customFields",
        "requiredConsents",
      ]);
    }
    setSubmitError("");
  };

  const handlePendingAttachmentFilesChange = (nextFiles) => {
    setPendingAttachmentFiles(Array.isArray(nextFiles) ? nextFiles : []);
    setSubmitError("");
  };

  const handleAttachmentVisibilityChange = (nextVisibility) => {
    setAttachmentVisibility(Boolean(nextVisibility));
    setSubmitError("");
  };

  const applyRegistrationTypeChange = (nextType) => {
    setFormData((prev) => ({
      ...prev,
      registrationType: nextType,
      ...createTypeSpecificReset(nextType),
    }));
    clearFieldErrors(["registrationType"]);

    if (nextType === "booking") {
      clearFieldErrors(LEGACY_TYPE_RESET_FIELDS);
    } else {
      clearFieldErrors(BOOKING_TYPE_RESET_FIELDS);
    }
  };

  const handleRegistrationTypeChange = (nextType) => {
    const normalizedNextType = nextType === "booking" ? "booking" : "legacy";
    if (normalizedNextType === formData.registrationType) return;

    const dataLossHints = getTypeSwitchDataLossHints(
      formData,
      normalizedNextType,
    );
    if (dataLossHints.length > 0) {
      const confirmed = window.confirm(
        [
          "Beim Wechsel des Event-Typs gehen typspezifische Eingaben verloren:",
          ...dataLossHints.map((hint) => `- ${hint}`),
          "",
          "Möchten Sie trotzdem wechseln?",
        ].join("\n"),
      );
      if (!confirmed) return;
    }

    applyRegistrationTypeChange(normalizedNextType);
  };

  const getStepValidationErrors = (stepKey) => {
    const stepErrors = {};

    if (stepKey === "eventType") {
      if (!["legacy", "booking"].includes(formData.registrationType)) {
        stepErrors.registrationType = "Bitte einen Event-Typ auswählen.";
      }
    }

    if (stepKey === "basicInfo") {
      const eventName = String(formData.eventName || "").trim();
      if (!eventName) {
        stepErrors.eventName = "Bitte einen Titel angeben.";
      }

      if (!formData.eventDateBegin) {
        stepErrors.eventDateBegin = "Bitte einen Beginn auswählen.";
      } else if (!dayjs(formData.eventDateBegin).isValid()) {
        stepErrors.eventDateBegin = "Beginn hat ein ungültiges Datumsformat.";
      }

      if (!formData.eventDateEnd) {
        stepErrors.eventDateEnd = "Bitte ein Ende auswählen.";
      } else if (!dayjs(formData.eventDateEnd).isValid()) {
        stepErrors.eventDateEnd = "Ende hat ein ungültiges Datumsformat.";
      }

      if (
        formData.eventDateBegin &&
        formData.eventDateEnd &&
        dayjs(formData.eventDateBegin).isValid() &&
        dayjs(formData.eventDateEnd).isValid() &&
        dayjs(formData.eventDateEnd).isBefore(dayjs(formData.eventDateBegin))
      ) {
        stepErrors.eventDateEnd = "Das Ende muss nach dem Beginn liegen.";
      }
    }

    if (
      stepKey === "pricingStandard" &&
      formData.registrationType === "legacy"
    ) {
      const feeBased = toBoolean(formData.isFeeBased, false);
      if (feeBased) {
        const pricingOptions = Array.isArray(formData.pricingOptions)
          ? formData.pricingOptions
          : [];

        if (!pricingOptions.length) {
          stepErrors.pricingOptions =
            "Bitte mindestens eine Preisoption für kostenpflichtige Events anlegen.";
        } else {
          const hasInvalidLabel = pricingOptions.some(
            (option) => !String(option?.label || "").trim(),
          );
          if (hasInvalidLabel) {
            stepErrors.pricingOptions =
              "Bitte einen Titel für jede Preisoption angeben.";
          }

          const hasInvalidAmount = pricingOptions.some((option) => {
            const amount = Number(option?.amount);
            return !Number.isFinite(amount) || amount <= 0;
          });
          if (hasInvalidAmount) {
            stepErrors.pricingOptions =
              "Bitte für jede Preisoption einen Betrag größer als 0 eingeben.";
          }
        }
      }

      const siblingEnabled =
        feeBased && toBoolean(formData.siblingDiscount?.enabled, false);
      if (siblingEnabled) {
        const siblingType =
          formData.siblingDiscount?.type === "fixed" ? "fixed" : "percentage";
        const siblingValue = Number(formData.siblingDiscount?.value);

        if (!Number.isFinite(siblingValue) || siblingValue <= 0) {
          stepErrors.siblingDiscount =
            "Bitte einen gültigen Geschwisterrabatt größer als 0 angeben.";
        } else if (siblingType === "percentage" && siblingValue > 100) {
          stepErrors.siblingDiscount =
            "Geschwisterrabatt in Prozent darf nicht größer als 100 sein.";
        }
      }
    }

    if (stepKey === "registration" && formData.registrationType === "booking") {
      const registrationConfig = formData.registrationConfig || {};
      const capacityMode = registrationConfig.capacityMode || "registrations";
      const validCapacityModes = ["registrations", "participants"];
      const eventPreset = String(registrationConfig.eventPreset || "custom");
      const validPresets = [
        "seminar",
        "ferienbetreuung",
        "camp_uebernachtung",
        "reisecamp",
        "custom",
      ];

      if (
        registrationConfig.registrationDeadline &&
        !dayjs(registrationConfig.registrationDeadline).isValid()
      ) {
        stepErrors.registrationConfig =
          "Die Anmeldefrist hat ein ungültiges Datumsformat.";
      }

      if (
        registrationConfig.maxRegistrations !== "" &&
        registrationConfig.maxRegistrations != null
      ) {
        const maxRegistrations = Number(registrationConfig.maxRegistrations);
        if (!Number.isFinite(maxRegistrations) || maxRegistrations <= 0) {
          stepErrors.registrationConfig =
            "Maximale Anmeldungen müssen größer als 0 sein oder leer bleiben.";
        }
      }

      if (!validCapacityModes.includes(capacityMode)) {
        stepErrors.registrationConfig = "Ungültiger Kapazitätsmodus.";
      }

      if (!validPresets.includes(eventPreset)) {
        stepErrors.registrationConfig = "Ungültige Eventvorlage.";
      }

      if (
        !toBoolean(formData.isPublic, false) &&
        toBoolean(registrationConfig.isPublicRegistrationEnabled, false)
      ) {
        stepErrors.registrationConfig =
          "Öffentliche Anmeldung ist nur bei öffentlicher Sichtbarkeit möglich.";
      }
    }

    if (stepKey === "sessions" && formData.registrationType === "booking") {
      const sessions = Array.isArray(formData.sessions)
        ? formData.sessions
        : [];
      if (sessions.length === 0) {
        stepErrors.sessions = "Bitte mindestens eine Session hinzufügen.";
      } else {
        const hasMissingLabel = sessions.some(
          (session) => !String(session?.label || "").trim(),
        );
        if (hasMissingLabel) {
          stepErrors.sessions =
            "Bitte für jede Session eine Bezeichnung angeben.";
        }

        const hasInvalidStart = sessions.some((session) => {
          if (!session?.dateStart) return false;
          return !dayjs(String(session.dateStart)).isValid();
        });
        if (hasInvalidStart) {
          stepErrors.sessions =
            "Mindestens ein Session-Beginn hat ein ungültiges Datumsformat.";
        }

        const hasInvalidEnd = sessions.some((session) => {
          if (!session?.dateEnd) return false;
          return !dayjs(String(session.dateEnd)).isValid();
        });
        if (hasInvalidEnd) {
          stepErrors.sessions =
            "Mindestens ein Session-Ende hat ein ungültiges Datumsformat.";
        }

        const hasEndBeforeStart = sessions.some((session) => {
          if (!session?.dateStart || !session?.dateEnd) return false;
          const start = dayjs(String(session.dateStart));
          const end = dayjs(String(session.dateEnd));
          if (!start.isValid() || !end.isValid()) return false;
          return end.isBefore(start);
        });
        if (hasEndBeforeStart) {
          stepErrors.sessions =
            "Session-Ende muss zeitlich nach dem Session-Beginn liegen.";
        }

        const hasInvalidCapacity = sessions.some((session) => {
          if (
            session?.maxParticipants == null ||
            session?.maxParticipants === ""
          ) {
            return false;
          }
          const capacity = Number(session.maxParticipants);
          return !Number.isFinite(capacity) || capacity <= 0;
        });
        if (hasInvalidCapacity) {
          stepErrors.sessions =
            "Session-Kapazität muss größer als 0 sein oder leer bleiben.";
        }
      }
    }

    if (
      stepKey === "pricingBooking" &&
      formData.registrationType === "booking"
    ) {
      const pricingRules = Array.isArray(formData.pricingRules)
        ? formData.pricingRules
        : [];

      if (pricingRules.length === 0) {
        stepErrors.pricingRules =
          "Bitte mindestens eine Preisregel hinzufügen.";
      } else {
        const hasMissingLabel = pricingRules.some(
          (rule) => !String(rule?.label || "").trim(),
        );
        if (hasMissingLabel) {
          stepErrors.pricingRules =
            "Bitte für jede Preisregel eine Bezeichnung angeben.";
        }

        const hasInvalidType = pricingRules.some(
          (rule) => !["fixed", "per_session", "package"].includes(rule?.type),
        );
        if (hasInvalidType) {
          stepErrors.pricingRules =
            "Mindestens eine Preisregel hat einen ungültigen Typ.";
        }

        const hasInvalidAmount = pricingRules.some((rule) => {
          const amount = Number(rule?.amount);
          return !Number.isFinite(amount) || amount <= 0;
        });
        if (hasInvalidAmount) {
          stepErrors.pricingRules =
            "Bitte für jede Preisregel einen Betrag größer als 0 eingeben.";
        }

        const hasInvalidMemberDiscount = pricingRules.some((rule) => {
          const memberDiscount = Number(rule?.memberDiscount || 0);
          return (
            !Number.isFinite(memberDiscount) ||
            memberDiscount < 0 ||
            memberDiscount > 100
          );
        });
        if (hasInvalidMemberDiscount) {
          stepErrors.pricingRules =
            "Mitgliederrabatt pro Regel muss zwischen 0 und 100 liegen.";
        }

        const hasMissingLinkedSessions = pricingRules.some((rule) => {
          if (rule?.type === "per_session") {
            return (
              !Array.isArray(rule?.sessionIds) || rule.sessionIds.length === 0
            );
          }
          if (rule?.type === "package") {
            const hasLinkedSessions =
              Array.isArray(rule?.sessionIds) && rule.sessionIds.length > 0;
            const hasMinSessions = Number(rule?.minSessions) >= 1;
            return !hasLinkedSessions && !hasMinSessions;
          }
          return false;
        });
        if (hasMissingLinkedSessions) {
          stepErrors.pricingRules =
            "Regeln vom Typ „Pro Session” benötigen verknüpfte Sessions. Paket-Regeln benötigen verknüpfte Sessions oder eine Mindestanzahl.";
        }

        const defaultCount = pricingRules.filter((rule) =>
          toBoolean(rule?.isDefault, false),
        ).length;
        if (defaultCount > 1) {
          stepErrors.pricingRules =
            "Es darf nur eine Standard-Preisregel geben.";
        }

        if (!stepErrors.pricingRules) {
          const packageCoverageValidation =
            validateBookingPackageRulesRestPricing({
              pricingRules,
              sessions: Array.isArray(formData.sessions)
                ? formData.sessions
                : [],
            });
          if (!packageCoverageValidation.isValid) {
            stepErrors.pricingRules = packageCoverageValidation.message;
          }
        }
      }

      const siblingDiscount =
        formData.registrationConfig?.discountRules?.siblingDiscount || {};
      const earlyBirdDiscount =
        formData.registrationConfig?.discountRules?.earlyBirdDiscount || {};

      if (toBoolean(siblingDiscount.enabled, false)) {
        const siblingPercentage = Number(siblingDiscount.percentage);
        if (
          !Number.isFinite(siblingPercentage) ||
          siblingPercentage <= 0 ||
          siblingPercentage > 100
        ) {
          stepErrors.discountRules =
            "Geschwisterrabatt muss zwischen 1 und 100 Prozent liegen.";
        }
      }

      if (toBoolean(earlyBirdDiscount.enabled, false)) {
        const earlyBirdType =
          earlyBirdDiscount.type === "fixed" ? "fixed" : "percentage";
        const earlyBirdPercentage = Number(earlyBirdDiscount.percentage);
        const earlyBirdAmount = Number(earlyBirdDiscount.amount);
        if (
          earlyBirdType === "percentage" &&
          (!Number.isFinite(earlyBirdPercentage) ||
            earlyBirdPercentage <= 0 ||
            earlyBirdPercentage > 100)
        ) {
          stepErrors.discountRules =
            "Frühbucherrabatt in Prozent muss zwischen 1 und 100 liegen.";
        } else if (
          earlyBirdType === "fixed" &&
          (!Number.isFinite(earlyBirdAmount) || earlyBirdAmount <= 0)
        ) {
          stepErrors.discountRules =
            "Frühbucherrabatt als Fixbetrag muss größer als 0 sein.";
        } else if (
          !earlyBirdDiscount.deadline ||
          !dayjs(String(earlyBirdDiscount.deadline)).isValid()
        ) {
          stepErrors.discountRules =
            "Bitte einen gültigen Frühbucher-Stichtag hinterlegen.";
        }
      }
    }

    if (stepKey === "extras" && formData.registrationType === "booking") {
      const customFields = Array.isArray(
        formData.registrationConfig?.customFields,
      )
        ? formData.registrationConfig.customFields
        : [];
      const requiredConsents = Array.isArray(
        formData.registrationConfig?.requiredConsents,
      )
        ? formData.registrationConfig.requiredConsents
        : [];

      const hasInvalidCustomField = customFields.some((field) => {
        const hasLabel = String(field?.label || "").trim().length > 0;
        const hasValidScope = ["booking", "participant"].includes(field?.scope);
        const hasValidType = [
          "text",
          "select",
          "multiselect",
          "checkbox",
          "date",
          "number",
        ].includes(field?.type);
        const needsOptions = ["select", "multiselect"].includes(field?.type);
        const hasOptions =
          Array.isArray(field?.options) && field.options.length > 0;

        if (!hasLabel || !hasValidScope || !hasValidType) return true;
        if (needsOptions && !hasOptions) return true;
        return false;
      });
      if (hasInvalidCustomField) {
        stepErrors.customFields =
          "Bitte alle Zusatzfelder vollständig ausfüllen (inkl. Optionen bei Auswahlfeldern).";
      }

      const hasInvalidConsent = requiredConsents.some(
        (consent) => !String(consent?.title || "").trim(),
      );
      if (hasInvalidConsent) {
        stepErrors.requiredConsents =
          "Bitte für jede Einwilligung mindestens einen Titel angeben.";
      }
    }

    return stepErrors;
  };

  const validateStep = (stepKey) => {
    const stepErrors = getStepValidationErrors(stepKey);
    const fields = STEP_ERROR_FIELDS[stepKey] || [];

    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        delete next[field];
      });
      return { ...next, ...stepErrors };
    });

    return Object.keys(stepErrors).length === 0;
  };

  const validateRequiredSteps = () => {
    const typeValid = validateStep("eventType");
    const basicInfoValid = validateStep("basicInfo");

    if (!typeValid) {
      const target = steps.findIndex((step) => step.key === "eventType");
      if (target >= 0) setActiveStep(target);
      return false;
    }
    if (!basicInfoValid) {
      const target = steps.findIndex((step) => step.key === "basicInfo");
      if (target >= 0) setActiveStep(target);
      return false;
    }

    if (formData.registrationType === "legacy") {
      const visibilityValid = validateStep("visibility");
      if (!visibilityValid) {
        const target = steps.findIndex((step) => step.key === "visibility");
        if (target >= 0) setActiveStep(target);
        return false;
      }

      const pricingValid = validateStep("pricingStandard");
      if (!pricingValid) {
        const target = steps.findIndex(
          (step) => step.key === "pricingStandard",
        );
        if (target >= 0) setActiveStep(target);
        return false;
      }
    }

    if (formData.registrationType === "booking") {
      const registrationValid = validateStep("registration");
      if (!registrationValid) {
        const target = steps.findIndex((step) => step.key === "registration");
        if (target >= 0) setActiveStep(target);
        return false;
      }

      const sessionsValid = validateStep("sessions");
      if (!sessionsValid) {
        const target = steps.findIndex((step) => step.key === "sessions");
        if (target >= 0) setActiveStep(target);
        return false;
      }

      const pricingValid = validateStep("pricingBooking");
      if (!pricingValid) {
        const target = steps.findIndex((step) => step.key === "pricingBooking");
        if (target >= 0) setActiveStep(target);
        return false;
      }

      const extrasValid = validateStep("extras");
      if (!extrasValid) {
        const target = steps.findIndex((step) => step.key === "extras");
        if (target >= 0) setActiveStep(target);
        return false;
      }
    }

    return true;
  };

  const toPayload = () => {
    const normalizedMaxParticipants = toNullableNumber(
      formData.maxParticipants,
    );
    const tags = String(formData.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const feeBased = toBoolean(formData.isFeeBased, false);
    const isPublic = toBoolean(formData.isPublic, false);
    const pricingOptionsRaw = Array.isArray(formData.pricingOptions)
      ? formData.pricingOptions.map((option) => ({
          optionId: option.optionId || undefined,
          label: toStringOrEmpty(option.label).trim(),
          description: toStringOrEmpty(option.description).trim(),
          amount: toNumberOr(option.amount, 0),
        }))
      : [];
    const pricingOptions = feeBased ? pricingOptionsRaw : [];
    const firstPricingOptionAmount = toNumberOr(
      pricingOptions[0]?.amount,
      toNumberOr(formData.feeAmount, 0),
    );
    const siblingDiscountType =
      formData.siblingDiscount?.type === "fixed" ? "fixed" : "percentage";
    const siblingDiscountValueBase = Math.max(
      0,
      toNumberOr(formData.siblingDiscount?.value, 0),
    );
    const siblingDiscountValue =
      siblingDiscountType === "percentage"
        ? Math.min(100, siblingDiscountValueBase)
        : siblingDiscountValueBase;
    const siblingDiscountEnabled =
      feeBased && toBoolean(formData.siblingDiscount?.enabled, false);

    const bookingPayload =
      formData.registrationType === "booking"
        ? {
            sessions: Array.isArray(formData.sessions)
              ? formData.sessions.map((session, index) => ({
                  sessionId:
                    session?.sessionId ||
                    (typeof session?.clientId === "string"
                      ? session.clientId
                      : undefined) ||
                    (typeof session?._id === "string"
                      ? session._id
                      : undefined),
                  label: toStringOrEmpty(session?.label).trim(),
                  dateStart: session?.dateStart || undefined,
                  dateEnd: session?.dateEnd || undefined,
                  maxParticipants: toNullableNumber(session?.maxParticipants),
                  sortOrder: Number.isFinite(Number(session?.sortOrder))
                    ? Number(session.sortOrder)
                    : index,
                }))
              : [],
            pricingRules: Array.isArray(formData.pricingRules)
              ? formData.pricingRules.map((rule) => ({
                  ruleId:
                    rule?.ruleId ||
                    (typeof rule?.clientId === "string"
                      ? rule.clientId
                      : undefined) ||
                    (typeof rule?._id === "string" ? rule._id : undefined),
                  label: toStringOrEmpty(rule?.label).trim(),
                  type: ["fixed", "per_session", "package"].includes(rule?.type)
                    ? rule.type
                    : "fixed",
                  amount: toNumberOr(rule?.amount, 0),
                  memberDiscount: toNumberOr(rule?.memberDiscount, 0),
                  sessionIds: Array.isArray(rule?.sessionIds)
                    ? rule.sessionIds.filter(Boolean)
                    : [],
                  minSessions:
                    rule?.type === "package" && Number(rule?.minSessions) >= 1
                      ? Math.floor(Number(rule.minSessions))
                      : null,
                  isDefault: toBoolean(rule?.isDefault, false),
                }))
              : [],
            registrationConfig: {
              isPublicRegistrationEnabled: isPublic
                ? toBoolean(
                    formData.registrationConfig?.isPublicRegistrationEnabled,
                    false,
                  )
                : false,
              requiresApproval: toBoolean(
                formData.registrationConfig?.requiresApproval,
                false,
              ),
              registrationDeadline:
                formData.registrationConfig?.registrationDeadline || undefined,
              maxRegistrations: toNullableNumber(
                formData.registrationConfig?.maxRegistrations,
              ),
              capacityMode:
                formData.registrationConfig?.capacityMode === "participants"
                  ? "participants"
                  : "registrations",
              eventPreset:
                toStringOrEmpty(
                  formData.registrationConfig?.eventPreset,
                ).trim() || undefined,
              customFields: Array.isArray(
                formData.registrationConfig?.customFields,
              )
                ? formData.registrationConfig.customFields.map(
                    (field, index) => ({
                      fieldId:
                        field?.fieldId ||
                        (typeof field?.clientId === "string"
                          ? field.clientId
                          : undefined) ||
                        (typeof field?._id === "string"
                          ? field._id
                          : undefined),
                      scope: field?.scope,
                      type: field?.type,
                      label: toStringOrEmpty(field?.label).trim(),
                      placeholder:
                        toStringOrEmpty(field?.placeholder).trim() || undefined,
                      options: Array.isArray(field?.options)
                        ? field.options
                            .map((option) => toStringOrEmpty(option).trim())
                            .filter(Boolean)
                        : [],
                      required: toBoolean(field?.required, false),
                      sensitive: toBoolean(field?.sensitive, false),
                      showCondition: field?.showCondition?.dependsOnFieldId
                        ? {
                            dependsOnFieldId: toStringOrEmpty(
                              field.showCondition.dependsOnFieldId,
                            ).trim(),
                            dependsOnValue: toStringOrEmpty(
                              field.showCondition.dependsOnValue,
                            ).trim(),
                          }
                        : undefined,
                      onlyForChildren: toBoolean(field?.onlyForChildren, false),
                      sortOrder: Number.isFinite(Number(field?.sortOrder))
                        ? Number(field.sortOrder)
                        : index,
                    }),
                  )
                : [],
              requiredConsents: Array.isArray(
                formData.registrationConfig?.requiredConsents,
              )
                ? formData.registrationConfig.requiredConsents.map(
                    (consent, index) => ({
                      consentId:
                        consent?.consentId ||
                        (typeof consent?.clientId === "string"
                          ? consent.clientId
                          : undefined) ||
                        (typeof consent?._id === "string"
                          ? consent._id
                          : undefined),
                      title: toStringOrEmpty(consent?.title).trim(),
                      text: toStringOrEmpty(consent?.text).trim(),
                      required: toBoolean(consent?.required, true),
                      sortOrder: Number.isFinite(Number(consent?.sortOrder))
                        ? Number(consent.sortOrder)
                        : index,
                    }),
                  )
                : [],
              bankTransferInfo: {
                enabled: toBoolean(
                  formData.registrationConfig?.bankTransferInfo?.enabled,
                  false,
                ),
                customText:
                  toStringOrEmpty(
                    formData.registrationConfig?.bankTransferInfo?.customText,
                  ).trim() || undefined,
              },
              sepaDirectDebitConsentText:
                toStringOrEmpty(
                  formData.registrationConfig?.sepaDirectDebitConsentText,
                ).trim() || DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT,
              publicBookingConfirmationText:
                toStringOrEmpty(
                  formData.registrationConfig?.publicBookingConfirmationText,
                ).trim() || DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT,
              publicBookingConfirmationTemplateMode:
                formData.registrationConfig
                  ?.publicBookingConfirmationTemplateMode === "html"
                  ? "html"
                  : "text",
              publicBookingConfirmationHtmlTemplate: toStringOrEmpty(
                formData.registrationConfig
                  ?.publicBookingConfirmationHtmlTemplate,
              ).trim(),
              discountRules: {
                siblingDiscount: {
                  enabled: toBoolean(
                    formData.registrationConfig?.discountRules?.siblingDiscount
                      ?.enabled,
                    false,
                  ),
                  percentage: Math.max(
                    0,
                    Math.min(
                      100,
                      toNumberOr(
                        formData.registrationConfig?.discountRules
                          ?.siblingDiscount?.percentage,
                        0,
                      ),
                    ),
                  ),
                },
                earlyBirdDiscount: {
                  enabled: toBoolean(
                    formData.registrationConfig?.discountRules
                      ?.earlyBirdDiscount?.enabled,
                    false,
                  ),
                  type:
                    formData.registrationConfig?.discountRules
                      ?.earlyBirdDiscount?.type === "fixed"
                      ? "fixed"
                      : "percentage",
                  percentage: Math.max(
                    0,
                    Math.min(
                      100,
                      toNumberOr(
                        formData.registrationConfig?.discountRules
                          ?.earlyBirdDiscount?.percentage,
                        0,
                      ),
                    ),
                  ),
                  amount: Math.max(
                    0,
                    toNumberOr(
                      formData.registrationConfig?.discountRules
                        ?.earlyBirdDiscount?.amount,
                      0,
                    ),
                  ),
                  deadline:
                    formData.registrationConfig?.discountRules
                      ?.earlyBirdDiscount?.deadline || undefined,
                },
              },
            },
          }
        : {};

    return {
      eventName: String(formData.eventName || "").trim(),
      eventType: String(formData.eventType || "").trim(),
      status: String(formData.status || "geplant"),
      eventDateBegin: formData.eventDateBegin || undefined,
      eventDateEnd: formData.eventDateEnd || undefined,
      description: toStringOrEmpty(formData.description).trim(),
      locationName: toStringOrEmpty(formData.locationName).trim(),
      city: toStringOrEmpty(formData.city).trim(),
      zip: toStringOrEmpty(formData.zip).trim(),
      street: toStringOrEmpty(formData.street).trim(),
      country: toStringOrEmpty(formData.country).trim(),
      maxParticipants: normalizedMaxParticipants,
      tags,
      isPublic,
      allowSelfRegistration: isPublic
        ? toBoolean(formData.allowSelfRegistration, true)
        : false,
      password: toStringOrEmpty(formData.password).trim(),
      isFeeBased: feeBased,
      feeAmount: feeBased ? firstPricingOptionAmount : 0,
      registrationType: formData.registrationType || "legacy",
      pricingOptions,
      siblingDiscount: {
        enabled: siblingDiscountEnabled,
        type: siblingDiscountType,
        value: siblingDiscountValue,
      },
      ...bookingPayload,
    };
  };

  const handleSubmit = async (forcePropagate = null) => {
    setSubmitError("");
    const isValid = validateRequiredSteps();
    if (!isValid) return;

    try {
      const payload = toPayload();
      const selectedAttachmentCount = pendingAttachmentFiles.length;
      let pricingPropagationInfo = null;
      let savedEventId = String(
        initialEvent?._id || initialEvent?.id || "",
      ).trim();

      if (isEdit) {
        // Prüfen ob Preise geändert wurden und Dialog noch nicht beantwortet
        if (forcePropagate === null && hasPricingChanged(initialEvent, formData)) {
          setShowPropagateDialog(true);
          return;
        }

        if (forcePropagate === true) {
          payload.propagatePricingToExistingBookings = true;
        }

        const updateResult = await updateEvent({
          id: initialEvent._id || initialEvent.id,
          ...payload,
        }).unwrap();

        pricingPropagationInfo = updateResult?.pricingPropagation || null;
      } else {
        const createResponse = await createEvent(payload).unwrap();
        savedEventId = String(
          createResponse?.event?._id || createResponse?.event?.id || "",
        ).trim();
      }

      if (selectedAttachmentCount > 0) {
        if (!savedEventId) {
          throw new Error(
            "Veranstaltung wurde gespeichert, aber die Event-ID für den Datei-Upload fehlt.",
          );
        }

        try {
          await uploadEventMedia({
            id: savedEventId,
            files: pendingAttachmentFiles,
            isVisibleToMembers: attachmentVisibility,
          }).unwrap();
          setPendingAttachmentFiles([]);
          setAttachmentVisibility(false);
        } catch (uploadError) {
          const uploadMessage =
            uploadError?.data?.message ||
            "Die Veranstaltung wurde gespeichert, aber die Anhänge konnten nicht hochgeladen werden.";

          dispatch(
            setDialog({
              type: "ERROR",
              title: "Veranstaltung gespeichert, Anhangs-Upload fehlgeschlagen",
              data: { errorText: uploadMessage },
            }),
          );
          onSaved?.();
          return;
        }
      }

      let successText =
        selectedAttachmentCount > 0
          ? isEdit
            ? `Änderungen wurden gespeichert. ${selectedAttachmentCount} Anhang/Anhänge wurden hochgeladen.`
            : `Die Veranstaltung wurde angelegt. ${selectedAttachmentCount} Anhang/Anhänge wurden hochgeladen.`
          : isEdit
          ? "Änderungen wurden gespeichert."
          : "Die Veranstaltung wurde angelegt.";

      if (pricingPropagationInfo) {
        const pr = pricingPropagationInfo;
        if (pr.updatedCount > 0 || pr.skippedOverrides > 0 || pr.skippedDeletedRule > 0) {
          successText += ` ${pr.updatedCount} Buchung(en) aktualisiert`;
          if (pr.skippedOverrides > 0) {
            successText += `, ${pr.skippedOverrides} mit Festpreis übersprungen`;
          }
          if (pr.skippedDeletedRule > 0) {
            successText += `, ${pr.skippedDeletedRule} mit gelöschter Regel übersprungen`;
          }
          successText += ".";
        }
      }

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: isEdit
            ? "Veranstaltung gespeichert"
            : "Veranstaltung erstellt",
          data: { successText },
        }),
      );
      onSaved?.();
    } catch (error) {
      const message =
        error?.data?.message || error?.message || "Speichern fehlgeschlagen.";
      setSubmitError(message);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: message },
        }),
      );
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep.key)) return;
    if (isLastStep) {
      handleSubmit(null);
      return;
    }

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setMaxVisitedStep((prev) => Math.max(prev, nextStep));
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onClose?.();
      return;
    }

    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const canOpenStep = (targetStep) =>
    targetStep <= maxVisitedStep || targetStep <= activeStep;

  const handleOpenStep = (targetStep) => {
    if (!canOpenStep(targetStep)) return;
    setActiveStep(targetStep);
  };

  const handleOpenStepByKey = (stepKey) => {
    const stepIndex = steps.findIndex((step) => step.key === stepKey);
    if (stepIndex < 0) return;
    handleOpenStep(stepIndex);
  };

  const renderStep = () => {
    if (currentStep.key === "eventType") {
      return (
        <StepEventType
          value={formData.registrationType}
          onChange={handleRegistrationTypeChange}
          disabledTypeSwitch={isRegistrationTypeLocked}
          lockReason={isRegistrationTypeLocked ? typeLockedReason : ""}
        />
      );
    }

    if (currentStep.key === "basicInfo") {
      return (
        <StepBasicInfo
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (
      currentStep.key === "visibility" &&
      formData.registrationType === "legacy"
    ) {
      return (
        <StepVisibility formData={formData} onChange={handleFieldChange} />
      );
    }

    if (
      currentStep.key === "pricingStandard" &&
      formData.registrationType === "legacy"
    ) {
      return (
        <StepPricingStandard
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (
      currentStep.key === "registration" &&
      formData.registrationType === "booking"
    ) {
      return (
        <StepRegistration
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (
      currentStep.key === "sessions" &&
      formData.registrationType === "booking"
    ) {
      return (
        <StepSessions
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (
      currentStep.key === "pricingBooking" &&
      formData.registrationType === "booking"
    ) {
      return (
        <StepPricingBooking
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (
      currentStep.key === "extras" &&
      formData.registrationType === "booking"
    ) {
      return (
        <StepExtras
          formData={formData}
          errors={errors}
          onChange={handleFieldChange}
        />
      );
    }

    if (currentStep.key === "attachments") {
      return (
        <StepAttachments
          eventId={String(initialEvent?._id || initialEvent?.id || "")}
          isEdit={isEdit}
          pendingFiles={pendingAttachmentFiles}
          onPendingFilesChange={handlePendingAttachmentFilesChange}
          isVisibleToMembers={attachmentVisibility}
          onVisibilityChange={handleAttachmentVisibilityChange}
          disabled={isSaving}
        />
      );
    }

    if (currentStep.key === "summary") {
      return (
        <StepSummary
          formData={formData}
          isEdit={isEdit}
          onEditStep={handleOpenStepByKey}
        />
      );
    }

    return (
      <StepPlaceholder label={currentStep.label} phase={currentStep.phase} />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullScreen
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h6">
              {isEdit
                ? "Veranstaltung bearbeiten"
                : "Neue Veranstaltung anlegen"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Schritt {activeStep + 1} von {steps.length}: {currentStep.label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={isSaving}>
            <Iconify icon="solar:close-circle-bold" width={22} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", py: 1 }}>
          {isSaving ? (
            <Box sx={{ mb: 2 }}>
              <LinearProgress color="secondary" />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Speichern läuft. Bitte warten...
              </Typography>
            </Box>
          ) : null}

          {isMobile ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 3, overflowX: "auto", pb: 1 }}
            >
              {steps.map((step, index) => {
                const active = index === activeStep;
                const clickable = canOpenStep(index);
                return (
                  <Chip
                    key={step.key}
                    label={`${index + 1}. ${step.label}`}
                    color={active ? "secondary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={
                      clickable ? () => handleOpenStep(index) : undefined
                    }
                    sx={{ whiteSpace: "nowrap" }}
                  />
                );
              })}
            </Stack>
          ) : (
            <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((step, index) => (
                <Step key={step.key} completed={index < maxVisitedStep}>
                  <StepButton
                    onClick={() => handleOpenStep(index)}
                    disabled={!canOpenStep(index)}
                  >
                    {step.label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}

          {submitError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          ) : null}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${formData.registrationType}-${currentStep.key}`}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleBack} disabled={isSaving}>
          {activeStep === 0 ? "Abbrechen" : "Zurück"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="secondary"
          onClick={handleNext}
          disabled={isSaving}
          endIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Iconify
                icon={
                  isLastStep
                    ? "solar:check-circle-bold"
                    : "solar:arrow-right-bold"
                }
                width={18}
              />
            )
          }
        >
          {isLastStep
            ? isEdit
              ? isSaving
                ? "Speichert..."
                : "Änderungen speichern"
              : isSaving
              ? "Erstellt..."
              : "Event erstellen"
            : "Weiter"}
        </Button>
      </DialogActions>

        {/* Preisänderung-Propagation Dialog */}
        <Dialog
          open={showPropagateDialog}
          onClose={() => setShowPropagateDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Preisänderung übernehmen?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Die Preisregeln wurden geändert. Soll die Änderung auch für
              bereits gebuchte Anmeldungen übernommen werden?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Buchungen mit manuell festgelegtem Preis werden nicht verändert.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowPropagateDialog(false);
                handleSubmit(false);
              }}
            >
              Nur neue Buchungen
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowPropagateDialog(false);
                handleSubmit(true);
              }}
            >
              Auch bestehende aktualisieren
            </Button>
          </DialogActions>
        </Dialog>
    </Dialog>
  );
}

EventFormStepper.propTypes = {
  open: PropTypes.bool,
  initialEvent: PropTypes.object,
  onSaved: PropTypes.func,
  onClose: PropTypes.func,
};

EventFormStepper.defaultProps = {
  open: false,
  initialEvent: null,
  onSaved: null,
  onClose: null,
};
